// Колоните на „По сервитьори" — списъкът и детайла по дни.
//
// Колоните за методи на плащане се ГЕНЕРИРАТ от `methodEntries` — всички
// активни методи в реда от настройките, както в „Продажби по периоди".
// Смесеното своя колона няма: разпределено е в „В брой" и „С карта", затова
// „В брой" е и числото за сверка на касата.
//
// Режимът е КЛИЕНТСКИ: `GET /reports/analytics/waiters` връща целия период
// наведнъж, затова сортирането, филтрите и редът „Общо" се смятат в браузъра.

import { useMemo } from "react";
import { createColumnHelper, type Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { formatPercent } from "../../../../components/dataTable";
import type {
  BarCellValue,
  TextCellValue,
} from "../../../../components/dataTable";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import type { WaiterDayRow, WaiterRow } from "./waitersMath";

const waiterHelper = createColumnHelper<WaiterRow>();
const dayHelper = createColumnHelper<WaiterDayRow>();

/** Колоната на метод в детайла по дни — по нея модалът смята ширината си. */
export const DAY_METHOD_COLUMN_WIDTH = 112;

/** Идентификаторът на колоната за един метод на плащане. */
const methodColumnId = (methodId: number) => `method_${methodId}`;

/**
 * Подредба на колоната „Дял" по САМАТА стойност. Без нея клетката-обект се
 * сортира по показания текст и „5.2 %" застава пред „37.4 %".
 */
const shareSortingFn = (
  rowA: Row<WaiterRow>,
  rowB: Row<WaiterRow>,
  columnId: string,
): number => {
  const a = (rowA.getValue(columnId) as BarCellValue).value;
  const b = (rowB.getValue(columnId) as BarCellValue).value;
  return a - b;
};

/**
 * Подредба на детайла по КАЛЕНДАРНИЯ ден. Клетката носи обект и общият
 * компаратор би сравнил показания текст — „01.08" застава пред „31.07".
 */
const daySortingFn = (rowA: Row<WaiterDayRow>, rowB: Row<WaiterDayRow>): number =>
  rowA.original.timestamp - rowB.original.timestamp;

type Options = {
  /** Колоната „Отстъпки" се появява само когато има откъде да се вземе. */
  showDiscounts: boolean;
  /** Същото за „Сторно" — иска правото за справка „Сторно". */
  showVoids: boolean;
};

export const useWaitersColumns = (
  methodEntries: MethodEntry[],
  { showDiscounts, showVoids }: Options,
) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      waiterHelper.accessor((row) => row.waiterName, {
        id: "waiter",
        header: t("reportsPage.waiter"),
        size: 190,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      waiterHelper.accessor((row) => row.bills, {
        id: "bills",
        header: t("reportsPage.bills"),
        size: 96,
        meta: {
          cell: { kind: "num" },
          filter: { kind: "numberRange" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      waiterHelper.accessor((row) => row.average, {
        id: "average",
        header: t("reportsPage.waitersAvgBill"),
        size: 128,
        // БЕЗ `total`: средните не се събират. Сборът им би бил число без
        // смисъл точно там, където читателят очаква „средното за всички".
        meta: {
          cell: { kind: "money", mutedZero: true },
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      ...methodEntries.map((method) =>
        waiterHelper.accessor((row) => row.byMethod[method.id] ?? 0, {
          id: methodColumnId(method.id),
          header: method.label,
          size: 126,
          meta: {
            label: method.label,
            cell: { kind: "money", mutedZero: true },
            total: "sum",
            excel: { header: method.label, format: "money" },
          },
        }),
      ),

      // Отстъпките и надбавките са ДВЕ колони, не една нето стойност: полето
      // `total_discount` е отрицателно при надбавка и слято в едно число
      // колоната „Отстъпки" щеше да показва по-малко от дадените, а при
      // повече надбавки — отрицателна сума с двоен минус.
      ...(showDiscounts
        ? [
            waiterHelper.accessor((row) => row.discounts, {
              id: "discounts",
              header: t("reportsPage.discountsReport"),
              size: 124,
              meta: {
                cell: { kind: "money", negative: true, mutedZero: true },
                total: "sum",
                hideBelow: "tablet",
                excel: { format: "money" },
              },
            }),
            waiterHelper.accessor((row) => row.surcharges, {
              id: "surcharges",
              header: t("reportsPage.surchargesKicker"),
              size: 124,
              meta: {
                cell: { kind: "money", mutedZero: true },
                total: "sum",
                // Надбавките са рядкост — колоната стои в панела, но не заема
                // място, докато някой не я потърси.
                defaultHidden: true,
                excel: { format: "money" },
              },
            }),
          ]
        : []),

      ...(showVoids
        ? [
            waiterHelper.accessor((row) => row.voids, {
              id: "voids",
              header: t("reportsPage.cancellations"),
              size: 124,
              meta: {
                cell: { kind: "money", negative: true },
                total: "sum",
                hideBelow: "tablet",
                excel: { format: "money" },
              },
            }),
          ]
        : []),

      waiterHelper.accessor((row) => row.total, {
        id: "total",
        header: t("reportsPage.waitersRevenue"),
        size: 136,
        meta: {
          cell: { kind: "money", strong: true },
          filter: { kind: "numberRange" },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),

      waiterHelper.accessor(
        (row): BarCellValue => ({
          value: row.total,
          max: row.periodTotal,
          label: formatPercent(row.share, 1),
        }),
        {
          id: "share",
          header: t("reportsPage.waitersShare"),
          size: 136,
          sortingFn: shareSortingFn,
          // Делът е производен от оборота — сборът му е винаги 100 % и в реда
          // „Общо" не казва нищо, затова колоната не участва в него.
          meta: { cell: { kind: "bar" }, hideBelow: "narrow" },
        },
      ),
    ],
    [t, methodEntries, showDiscounts, showVoids],
  );
};

/**
 * Колоните на детайла по дни — същият ред, само че „Дата" вместо „Сервитьор".
 * По-тесни от тези отвън, за да остане модалът възможно най-тесен — той
 * смята ширината си от тях (`DAY_METHOD_COLUMN_WIDTH`), за да се види цялата
 * таблица без скрол настрани.
 */
export const useWaiterDayColumns = (methodEntries: MethodEntry[]) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      dayHelper.accessor(
        (row): TextCellValue => ({
          primary: row.dateLabel,
          secondary: row.weekday,
        }),
        {
          id: "date",
          header: t("reportsPage.date"),
          size: 140,
          sortingFn: daySortingFn,
          meta: {
            cell: { kind: "text", lines: 2 },
            alwaysVisible: true,
            mobileRole: "title",
            excel: { format: "text" },
          },
        },
      ),

      dayHelper.accessor((row) => row.bills, {
        id: "bills",
        header: t("reportsPage.bills"),
        size: 88,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      dayHelper.accessor((row) => row.average, {
        id: "average",
        header: t("reportsPage.waitersAvgBill"),
        size: 128,
        meta: {
          cell: { kind: "money", mutedZero: true },
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      ...methodEntries.map((method) =>
        dayHelper.accessor((row) => row.byMethod[method.id] ?? 0, {
          id: methodColumnId(method.id),
          header: method.label,
          size: DAY_METHOD_COLUMN_WIDTH,
          meta: {
            label: method.label,
            cell: { kind: "money", mutedZero: true },
            total: "sum",
            excel: { header: method.label, format: "money" },
          },
        }),
      ),

      dayHelper.accessor((row) => row.total, {
        id: "total",
        header: t("reportsPage.waitersRevenue"),
        size: 136,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),
    ],
    [t, methodEntries],
  );
};
